import type { Container } from "pixi.js";
import type { ReelAnticipationFrame } from "./ReelAnticipationFrame";
import type { ReelAnticipationScaleTuning } from "./ReelMotionTuning";
import type { ReelRollAudio } from "./ReelRollAudio";
import type { ComputedGridLayout, PresentationTaskRunner } from "@slot/presentation";

/** What the anticipation reads from the grid: the layout for the frame's spot, the two roots it drives. */
export interface ReelAnticipationGrid {
  readonly gridColumnCount: number;
  readonly gridRowCount: number;
  readonly gridRoot: Container | null;
  readonly layoutRoot: Container | null;
  getComputedLayout(): ComputedGridLayout;
}

export interface ReelAnticipationDeps {
  grid: ReelAnticipationGrid;
  taskRunner: PresentationTaskRunner | null;
  roll: ReelRollAudio;
  /** The near-miss frame shown over a teasing reel, and the overlay node it mounts in. */
  frame: { create: () => ReelAnticipationFrame; parent: Container } | null;
  /** The game's session hooks: `onStart(ordinal)` per teasing reel (1-based), `onEnd` once the last one lands. */
  listener: { onStart?(ordinal: number): void; onEnd?(): void } | null;
  /** The grid zoom while a reel teases; absent = no zoom. */
  scale: () => ReelAnticipationScaleTuning | undefined;
}

/**
 * A spin's anticipation: the frame over the teasing reel, the grid zoom creeping up while any
 * flagged reel teases and snapping back when the last one lands, the anticipation roll, and the
 * game's session hooks. One reel teases at a time (reels land left to right).
 */
export class ReelAnticipation {
  private frameInstance: ReelAnticipationFrame | null = null;
  private frameColumn: number | null = null;
  /** The highest flagged reel in range: the last to land, which ends the session; -1 when none. */
  private lastFlaggedColumn = -1;
  private startedCount = 0;
  private readonly zoomHome = { x: 1, y: 1 };
  private zoomActive = false;
  private zoomDisplaced = false;
  private zoomGeneration = 0;
  /**
   * The tuned drop home is playing: the end-of-spin teardown runs meanwhile and must not cut it. Only the
   * snap-back that still holds the current generation clears it; a creep that takes the zoom over clears it too.
   */
  private snappingBack = false;

  public constructor(private readonly deps: ReelAnticipationDeps) {}

  /** The finals are known: which reels tease this spin (out-of-range flags are ignored, so none can strand the zoom). */
  public begin(anticipatedColumns: number[] | undefined): void {
    let max = -1;
    for (const c of anticipatedColumns ?? []) {
      if (Number.isInteger(c) && c >= 0 && c < this.deps.grid.gridColumnCount && c > max) max = c;
    }
    this.lastFlaggedColumn = max;
  }

  /** A flagged reel entered its slow beat: the frame over it, the zoom creeping, the session (and its roll) open. */
  public columnTeases(column: number): void {
    this.showFrame(column);
    this.startZoom();
    this.startedCount += 1;
    if (this.startedCount === 1) this.deps.roll.startAnticipation();
    this.deps.listener?.onStart?.(this.startedCount);
  }

  /** A reel landed: its frame hides; the last flagged reel ends the zoom and the session. */
  public columnLanded(column: number): void {
    this.hideFrame(column);
    if (column === this.lastFlaggedColumn) {
      this.endZoom();
      this.endSession();
    }
  }

  /**
   * Teardown: frame hidden, session closed, zoom home. A snap-back already playing finishes on its
   * own (the spin's render runs this while it plays); any other zoom snaps home at once. Idempotent.
   */
  public reset(): void {
    this.hideFrame();
    this.zoomActive = false;
    if (!this.snappingBack) {
      this.zoomGeneration += 1;
      if (this.zoomDisplaced) {
        this.zoomDisplaced = false;
        const node = this.deps.grid.layoutRoot;
        if (node && !node.destroyed) node.scale.set(this.zoomHome.x, this.zoomHome.y);
      }
    }
    this.endSession();
  }

  private endSession(): void {
    if (this.startedCount > 0) {
      this.deps.roll.stopAnticipation();
      this.deps.listener?.onEnd?.();
    }
    this.startedCount = 0;
  }

  // ── The frame ─────────────────────────────────────────────────────────────

  private showFrame(column: number): void {
    const frame = this.deps.frame;
    if (!frame || frame.parent.destroyed) return;
    if (!this.frameInstance || this.frameInstance.node.destroyed) {
      this.frameInstance = frame.create();
      frame.parent.addChild(this.frameInstance.node);
    }
    // Centred on the reel: the column's grid-local centre in the overlay's space.
    const layout = this.deps.grid.getComputedLayout();
    const centre = { x: layout.originX + column * layout.cellWidth + (layout.columnOffsets[column] ?? 0), y: layout.originY + ((this.deps.grid.gridRowCount - 1) * layout.cellHeight) / 2 };
    this.frameInstance.node.position.copyFrom(frame.parent.toLocal(centre, this.deps.grid.gridRoot ?? frame.parent));
    this.frameInstance.node.visible = true;
    this.frameInstance.playIn();
    this.frameColumn = column;
  }

  /** With `column`, only the reel showing the frame hides it (a land elsewhere leaves an active tease). */
  private hideFrame(column?: number): void {
    if (column !== undefined && this.frameColumn !== column) return;
    if (this.frameInstance && !this.frameInstance.node.destroyed) this.frameInstance.playOut();
    this.frameColumn = null;
  }

  // ── The zoom: the layout root's scale, its own generation token (the land bounce rides the position) ──

  private startZoom(): void {
    const scale = this.deps.scale();
    const node = this.deps.grid.layoutRoot;
    if (!scale || !node || node.destroyed || !this.deps.taskRunner || this.zoomActive) return;
    this.zoomActive = true;
    void this.creep(node, scale, this.deps.taskRunner);
  }

  /**
   * A linear creep toward the cap (the resting scale × maxScale); holds at the cap until the snap-back. A creep that
   * starts while the previous spin's drop home still plays takes the zoom over from where the drop is, keeping the resting scale.
   */
  private async creep(node: Container, scale: ReelAnticipationScaleTuning, runner: PresentationTaskRunner): Promise<void> {
    // While displaced (a snap-back still dropping), the node's scale is not the resting one: keep the home.
    if (!this.zoomDisplaced) {
      this.zoomHome.x = node.scale.x;
      this.zoomHome.y = node.scale.y;
    }
    this.zoomDisplaced = true;
    // The creep owns the zoom now: a snap-back it silences no longer shields the teardown.
    this.snappingBack = false;
    const generation = ++this.zoomGeneration;
    const cap = Math.max(1, scale.maxScale);
    const start = { x: node.scale.x, y: node.scale.y };
    const target = { x: this.zoomHome.x * cap, y: this.zoomHome.y * cap };
    const seconds = Math.max(0.001, (cap - 1) / Math.max(0.0001, scale.creepPerSecond));
    await runner.tweenProgress(
      seconds,
      (k) => {
        if (generation === this.zoomGeneration && !node.destroyed) node.scale.set(start.x + (target.x - start.x) * k, start.y + (target.y - start.y) * k);
      },
      undefined,
      { easing: "linear" },
    );
  }

  private endZoom(): void {
    const node = this.deps.grid.layoutRoot;
    if (!this.zoomActive || !node || node.destroyed || !this.deps.taskRunner) return;
    this.zoomActive = false;
    const scale = this.deps.scale();
    if (!scale) {
      this.reset();
      return;
    }
    void this.snapBack(node, scale, this.deps.taskRunner);
  }

  /** The fast drop back to the resting scale after an optional held beat. */
  private async snapBack(node: Container, scale: ReelAnticipationScaleTuning, runner: PresentationTaskRunner): Promise<void> {
    const generation = ++this.zoomGeneration;
    const home = this.zoomHome;
    this.snappingBack = true;
    try {
      const startDelay = Math.max(0, scale.downStartDelaySeconds ?? 0);
      if (startDelay > 0) {
        await runner.sleep(startDelay);
        if (generation !== this.zoomGeneration) return;
      }
      const start = { x: node.scale.x, y: node.scale.y };
      await runner.tweenProgress(Math.max(0.001, scale.downSeconds), (k) => {
        if (generation === this.zoomGeneration && !node.destroyed) node.scale.set(start.x + (home.x - start.x) * k, start.y + (home.y - start.y) * k);
      });
      if (generation !== this.zoomGeneration) return;
      if (!node.destroyed) node.scale.set(home.x, home.y);
      this.zoomDisplaced = false;
    } finally {
      // A snap-back silenced by a newer creep leaves the flag alone: that creep cleared it, and a newer snap-back may hold it now.
      if (generation === this.zoomGeneration) this.snappingBack = false;
    }
  }
}
